import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { 
  PhoneIcon, 
  ClockIcon, 
  EyeIcon,
  XIcon,
  PlusIcon,
  SearchIcon,
  ArchiveBoxIcon,
  PlayIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon
} from '../components/Icons'

interface UnmatchedCall {
  id: number
  from_phone: string
  to_phone: string
  direction: 'inbound' | 'outbound'
  call_date: string
  duration_seconds?: number
  transcript?: string
  summary?: string
  key_topics?: string[]
  sentiment?: 'positive' | 'negative' | 'neutral'
  is_archived: boolean
  notes?: string
  call_started_at?: string
  call_status?: string
}

interface CallsStats {
  processing: {
    total: number
    completed: number
    failed: number
    pending: number
  }
  unmatched: {
    active: number
    archived: number
  }
  recent: {
    last24Hours: number
  }
  worker: {
    isRunning: boolean
    intervalMinutes: number
    nextRun?: string
  }
}

const Calls: React.FC = () => {
  const { t } = useTranslation()
  const [calls, setCalls] = useState<UnmatchedCall[]>([])
  const [stats, setStats] = useState<CallsStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCall, setSelectedCall] = useState<UnmatchedCall | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [processing, setProcessing] = useState(false)

  const telnyxServiceUrl = (import.meta as any).env?.VITE_TELNYX_SERVICE_URL || 'http://localhost:3001'

  // Fetch calls
  const fetchCalls = async (page: number = 1, archived: boolean = false) => {
    try {
      setLoading(true)
      const response = await fetch(
        `${telnyxServiceUrl}/api/calls/unmatched?page=${page}&limit=20&archived=${archived}`
      )
      
      if (!response.ok) {
        throw new Error('Failed to fetch calls')
      }

      const data = await response.json()
      setCalls(data.calls || [])
      setTotalPages(data.pagination?.totalPages || 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch calls')
    } finally {
      setLoading(false)
    }
  }

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${telnyxServiceUrl}/api/calls/stats`)
      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  // Archive/unarchive call
  const toggleArchiveCall = async (callId: number, archived: boolean, notes?: string) => {
    try {
      setProcessing(true)
      const response = await fetch(`${telnyxServiceUrl}/api/calls/unmatched/${callId}/archive`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ archived, notes })
      })

      if (!response.ok) {
        throw new Error('Failed to update call')
      }

      // Refresh the current page
      await fetchCalls(currentPage, showArchived)
      await fetchStats()
      
      if (selectedCall?.id === callId) {
        setSelectedCall(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update call')
    } finally {
      setProcessing(false)
    }
  }

  // Trigger manual processing
  const triggerProcessing = async () => {
    try {
      setProcessing(true)
      const response = await fetch(`${telnyxServiceUrl}/api/calls/worker/trigger`, {
        method: 'POST'
      })

      if (!response.ok) {
        throw new Error('Failed to trigger processing')
      }

      await fetchStats()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger processing')
    } finally {
      setProcessing(false)
    }
  }

  useEffect(() => {
    // Feature is disabled for now - set loading to false
    setLoading(false)
    // fetchCalls(currentPage, showArchived)
    // fetchStats()
  }, [currentPage, showArchived])

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const getSentimentColor = (sentiment?: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/20'
      case 'negative': return 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/20'
      default: return 'text-gray-600 bg-gray-50 dark:text-gray-400 dark:bg-gray-900/20'
    }
  }

  // Feature is temporarily disabled - show coming soon message
  const isDisabled = true

  if (loading && !calls.length) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (isDisabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <PhoneIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              {t('calls.comingSoon')}
            </h2>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('calls.comingSoonDescription')}
            </p>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                💡 {t('calls.comingSoonHint')}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          📞 Call Recordings
        </h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={triggerProcessing}
            disabled={processing}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {processing ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <PlayIcon className="h-4 w-4" />
            )}
            <span>Sync Now</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start space-x-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 mt-2"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Processed</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.processing.completed}</p>
              </div>
              <PhoneIcon className="h-8 w-8 text-gray-700" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unmatched</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.unmatched.active}</p>
              </div>
              <DocumentTextIcon className="h-8 w-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Last 24h</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.recent.last24Hours}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Worker Status</p>
                <p className={`text-sm font-medium ${stats.worker.isRunning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {stats.worker.isRunning ? 'Running' : 'Stopped'}
                </p>
              </div>
              <div className={`h-3 w-3 rounded-full ${stats.worker.isRunning ? 'bg-green-500' : 'bg-red-500'}`} />
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setShowArchived(false)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            !showArchived
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Active Calls ({stats?.unmatched.active || 0})
        </button>
        <button
          onClick={() => setShowArchived(true)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            showArchived
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Archived ({stats?.unmatched.archived || 0})
        </button>
      </div>

      {/* Calls List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {calls.length === 0 ? (
          <div className="p-8 text-center">
            <PhoneIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {showArchived ? 'No archived calls' : 'No unmatched calls'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {showArchived 
                ? 'No calls have been archived yet.' 
                : 'All recent calls have been automatically matched to deals.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {calls.map((call) => (
              <div
                key={call.id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                onClick={() => setSelectedCall(call)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`p-2 rounded-lg ${call.direction === 'inbound' ? 'bg-green-100 dark:bg-green-900/20' : 'bg-blue-100 dark:bg-blue-900/20'}`}>
                        <PhoneIcon className={`h-4 w-4 ${call.direction === 'inbound' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-gray-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {call.direction === 'inbound' ? 'Incoming Call' : 'Outgoing Call'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          From: {call.from_phone} → To: {call.to_phone}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <span>{format(new Date(call.call_date), 'MMM d, yyyy - h:mm a')}</span>
                      <span>Duration: {formatDuration(call.duration_seconds)}</span>
                      {call.sentiment && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(call.sentiment)}`}>
                          {call.sentiment}
                        </span>
                      )}
                    </div>

                    {call.summary && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3 line-clamp-2">
                        {call.summary}
                      </p>
                    )}

                    {call.key_topics && call.key_topics.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {call.key_topics.slice(0, 3).map((topic, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300 rounded-full"
                          >
                            {topic}
                          </span>
                        ))}
                        {call.key_topics.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs text-gray-500 dark:text-gray-400 rounded-full">
                            +{call.key_topics.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedCall(call)
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleArchiveCall(call.id, !call.is_archived)
                      }}
                      disabled={processing}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                    >
                      <ArchiveBoxIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed hover:text-gray-900 dark:hover:text-white"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span>Previous</span>
            </button>

            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed hover:text-gray-900 dark:hover:text-white"
            >
              <span>Next</span>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Call Recording Details
              </h2>
              <button
                onClick={() => setSelectedCall(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {/* Call Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Call Information</h3>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-600 dark:text-gray-400">Direction:</span> {selectedCall.direction}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">From:</span> {selectedCall.from_phone}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">To:</span> {selectedCall.to_phone}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Date:</span> {format(new Date(selectedCall.call_date), 'MMM d, yyyy - h:mm a')}</p>
                      <p><span className="text-gray-600 dark:text-gray-400">Duration:</span> {formatDuration(selectedCall.duration_seconds)}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Analysis</h3>
                    <div className="space-y-1 text-sm">
                      {selectedCall.sentiment && (
                        <p>
                          <span className="text-gray-600 dark:text-gray-400">Sentiment:</span>
                          <span className={`ml-2 px-2 py-1 rounded text-xs ${getSentimentColor(selectedCall.sentiment)}`}>
                            {selectedCall.sentiment}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {selectedCall.summary && (
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Summary</h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      {selectedCall.summary}
                    </p>
                  </div>
                )}

                {/* Key Topics */}
                {selectedCall.key_topics && selectedCall.key_topics.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Key Topics</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedCall.key_topics.map((topic, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-sm rounded-full"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {selectedCall.transcript && (
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2">Transcript</h3>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg max-h-96 overflow-y-auto">
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {selectedCall.transcript}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => toggleArchiveCall(selectedCall.id, !selectedCall.is_archived)}
                    disabled={processing}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {selectedCall.is_archived ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    onClick={() => setSelectedCall(null)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Calls
